import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Input, LayoutFrame, registerHomeTile, useAppHeader } from '@room/shared-ui';
import { useToast } from '@room/shared-ui/components/UI';
import { Button } from '@room/shared-ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@room/shared-ui/components/ui/card';
import { adminAPI } from '../../../lib/api';
import styles from '../AdminPage.module.css';
import { adminTileVisible } from '../homeTiles';

type SystemSettingsPayload = {
  guestLoginEnabled: boolean;
  paymentsEnabled: boolean;
  groupCreationEnabled: boolean;
  fileUploadEnabled: boolean;
  maxFileSizeMB: number;
  maxGroupMembers: number;
};

registerHomeTile({
  id: 'home-admin-system-settings',
  label: 'System Settings',
  icon: '🛠️',
  route: '/admin/system-settings',
  section: 'main',
  order: 80,
  visible: adminTileVisible,
});

const defaultSettings: SystemSettingsPayload = {
  guestLoginEnabled: true,
  paymentsEnabled: true,
  groupCreationEnabled: true,
  fileUploadEnabled: true,
  maxFileSizeMB: 10,
  maxGroupMembers: 50,
};

export default function SystemSettingsPage() {
  const { setHeaderConfig } = useAppHeader();
  const { addToast } = useToast();
  const [formState, setFormState] = useState<SystemSettingsPayload>(defaultSettings);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'system-settings'],
    queryFn: () => adminAPI.getSystemSettings(),
  });

  const payload = data?.data ?? null;

  useEffect(() => {
    setHeaderConfig({
      title: 'System Settings',
      subtitle: 'Control global toggles and limits for the platform.',
    });
    return () => setHeaderConfig(null);
  }, [setHeaderConfig]);

  useEffect(() => {
    if (payload) {
      setFormState({
        guestLoginEnabled: payload.guestLoginEnabled ?? true,
        paymentsEnabled: payload.paymentsEnabled ?? true,
        groupCreationEnabled: payload.groupCreationEnabled ?? true,
        fileUploadEnabled: payload.fileUploadEnabled ?? true,
        maxFileSizeMB: payload.maxFileSizeMB ?? 10,
        maxGroupMembers: payload.maxGroupMembers ?? 50,
      });
    }
  }, [payload]);

  const mutation = useMutation({
    mutationFn: (nextPayload: SystemSettingsPayload) => adminAPI.saveSystemSettings(nextPayload),
    onSuccess: () => {
      addToast('System settings updated successfully.', 'success');
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : 'Failed to update settings.';
      addToast(message, 'error');
    },
  });

  const lastUpdated = useMemo(() => {
    if (!payload?.updatedAt) return 'Not available';
    return new Date(payload.updatedAt).toLocaleString();
  }, [payload?.updatedAt]);

  const handleToggle = (key: keyof SystemSettingsPayload) => {
    setFormState((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleNumberChange = (key: keyof SystemSettingsPayload, value: string) => {
    const parsed = Number(value);
    setFormState((prev) => ({
      ...prev,
      [key]: Number.isNaN(parsed) ? prev[key] : parsed,
    }));
  };

  const handleSubmit = () => {
    mutation.mutate(formState);
  };

  return (
    <LayoutFrame withSection={false} flush className={styles.page}>
      {error instanceof Error ? (
        <Alert variant="error" title="Unable to load system settings">
          {error.message}
        </Alert>
      ) : null}
      <Card>
        <CardHeader>
          <CardTitle>System Controls</CardTitle>
        </CardHeader>
        <CardContent className={styles.section}>
          <div className={styles.formGrid}>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={formState.guestLoginEnabled}
                onChange={() => handleToggle('guestLoginEnabled')}
              />
              Guest login enabled
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={formState.paymentsEnabled}
                onChange={() => handleToggle('paymentsEnabled')}
              />
              Payments enabled
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={formState.groupCreationEnabled}
                onChange={() => handleToggle('groupCreationEnabled')}
              />
              Group creation enabled
            </label>
            <label className={styles.checkboxRow}>
              <input
                type="checkbox"
                checked={formState.fileUploadEnabled}
                onChange={() => handleToggle('fileUploadEnabled')}
              />
              File upload enabled
            </label>
          </div>

          <div className={styles.formGrid}>
            <Input
              label="Max file size (MB)"
              type="number"
              min={1}
              max={100}
              value={formState.maxFileSizeMB}
              onChange={(event) => handleNumberChange('maxFileSizeMB', event.target.value)}
              fullWidth
            />
            <Input
              label="Max group members"
              type="number"
              min={2}
              max={500}
              value={formState.maxGroupMembers}
              onChange={(event) => handleNumberChange('maxGroupMembers', event.target.value)}
              fullWidth
            />
          </div>

          <p className={`${styles.helperText} content-wrap`}>Last updated: {lastUpdated}</p>
          <div className={styles.inlineActions}>
            <Button onClick={handleSubmit} disabled={isLoading} loading={mutation.isPending}>
              Save changes
            </Button>
          </div>
        </CardContent>
      </Card>
    </LayoutFrame>
  );
}
